import {
  Calculator,
  Droplets,
  Scissors,
  Bug,
  Sprout,
  TestTube,
  MapPin,
  Calendar,
  Wallet,
  Maximize2,
  Thermometer,
  GitCompare,
  FlaskConical,
  Scale,
  Droplet,
  CalendarClock,
  Layers,
  LayoutGrid,
  Shovel,
  Wind,
  type LucideIcon,
} from 'lucide-react'
import { FERTILIZER_PRODUCTS } from '@/data/fertilizer-products'

/**
 * Single source of truth for the /tools index. Adding a tool means adding one
 * entry here. Routes under app/tools/* that aren't in this manifest will be
 * orphaned (no internal link from /tools, no JSON-LD ItemList entry).
 *
 * This was previously a hand-maintained array inside app/tools/page.tsx — the
 * extraction is the [[features/calculators|calculators]] flag fix from the
 * squash plan (Tier B #26).
 */
export interface ToolManifestEntry {
  id: string
  name: string
  description: string
  icon: LucideIcon
  href: string
  isActive: boolean
  estimatedTime: string
  category: string
  featured: boolean
  hasEmbed: boolean
  relatedTools?: string[]
}

// Product-specific fertilizer calculators (Milorganite, the Scotts family, PGF,
// Ironite, Lesco, Simple Lawn Solutions). Generated from the shared product
// catalog so the /tools directory surfaces them as their own "Product
// Calculators" group (with ItemList JSON-LD + internal links) instead of leaving
// them off-manifest and link-orphaned. Each route is the Fertilizer Schedule
// Calculator locked to one product; the hub itself keeps its own entry below.
const PRODUCT_CALCULATOR_TOOLS: ToolManifestEntry[] = FERTILIZER_PRODUCTS.map((product) => ({
  id: product.slug,
  name: product.metaTitle.split('|')[0].trim(),
  description: product.metaDescription,
  icon: Sprout,
  href: `/tools/${product.slug}`,
  isActive: true,
  estimatedTime: '1 minute',
  category: 'Products',
  featured: false,
  hasEmbed: false,
  relatedTools: ['fertilizer-schedule-calculator', 'fertilizer-calculator', 'fertilizer-rate-calculator'],
}))

export const TOOLS_MANIFEST: ToolManifestEntry[] = [
  {
    id: 'lawn-size-calculator',
    name: 'Lawn Size Calculator',
    description:
      'Free lawn size calculator using satellite imagery. Enter your address, trace your yard on the map, and get exact square footage for fertilizer, seed, and sod amounts.',
    icon: Maximize2,
    href: '/tools/lawn-size-calculator',
    isActive: true,
    estimatedTime: '2 minutes',
    category: 'Basics',
    featured: true,
    hasEmbed: false,
    relatedTools: ['fertilizer-calculator', 'watering-schedule', 'sod-calculator'],
  },
  {
    id: 'soil-temperature',
    name: 'Soil Temperature Tracker',
    description:
      'Monitor current soil temperatures and compare to 5-year and 10-year historical averages. Use it to time pre-emergent herbicides, overseeding, and fertilizer applications.',
    icon: Thermometer,
    href: '/tools/soil-temperature',
    isActive: true,
    estimatedTime: '1 minute',
    category: 'Planning',
    featured: false,
    hasEmbed: false,
    relatedTools: ['herbicide-timing', 'seeding-calculator', 'fertilizer-calculator'],
  },
  {
    id: 'seeding-calculator',
    name: 'Is It Too Late To Seed?',
    description:
      "Find out if it's too late to seed your lawn based on your location and grass type. Get personalized seeding windows and timing recommendations.",
    icon: Calculator,
    href: '/tools/seeding-calculator',
    isActive: true,
    estimatedTime: '2 minutes',
    category: 'Seasonal',
    featured: false,
    hasEmbed: false,
    relatedTools: ['overseeding-calculator', 'sod-calculator', 'soil-temperature'],
  },
  {
    id: 'fertilizer-calculator',
    name: 'Fertilizer Calculator',
    description:
      'Calculate the right amount of fertilizer for your lawn size and grass type. Get NPK recommendations and application timing.',
    icon: Sprout,
    href: '/tools/fertilizer-calculator',
    isActive: true,
    estimatedTime: '3 minutes',
    category: 'Seasonal',
    featured: false,
    hasEmbed: true,
    relatedTools: ['watering-schedule', 'soil-test-analyzer', 'lime-calculator', 'spreader-settings'],
  },
  {
    id: 'overseeding-calculator',
    name: 'Overseeding Calculator',
    description:
      'Find out how much grass seed to overseed and exactly when. Enter your ZIP, grass type, and lawn size for pounds of seed, bag counts, and your best overseeding window with a countdown.',
    icon: Sprout,
    href: '/tools/overseeding-calculator',
    isActive: true,
    estimatedTime: '2 minutes',
    category: 'Seasonal',
    featured: false,
    hasEmbed: false,
    relatedTools: ['seeding-calculator', 'aeration-timing-tool', 'topsoil-calculator'],
  },
  {
    id: 'sod-calculator',
    name: 'Sod Calculator',
    description:
      'Turn your lawn size into pallets, rolls, and pieces of sod with a 10% waste allowance. Estimate material and professional install cost by region, and see how sod compares to seeding.',
    icon: Layers,
    href: '/tools/sod-calculator',
    isActive: true,
    estimatedTime: '2 minutes',
    category: 'Planning',
    featured: false,
    hasEmbed: false,
    relatedTools: ['lawn-size-calculator', 'overseeding-calculator', 'fertilizer-calculator', 'artificial-turf-cost-calculator'],
  },
  {
    id: 'artificial-turf-cost-calculator',
    name: 'Artificial Turf Cost Calculator',
    description:
      'Estimate the cost to install artificial grass by area, turf grade, and region. Compare DIY vs professional install ranges for turf, base, infill, labor, and old-lawn removal. Ranges are estimates, so get local quotes.',
    icon: LayoutGrid,
    href: '/tools/artificial-turf-cost-calculator',
    isActive: true,
    estimatedTime: '2 minutes',
    category: 'Planning',
    featured: false,
    hasEmbed: false,
    relatedTools: ['sod-calculator', 'lawn-cost-estimator', 'lawn-size-calculator'],
  },
  {
    id: 'topsoil-calculator',
    name: 'Topsoil & Lawn Leveling Calculator',
    description:
      'Turn your lawn size and depth into cubic yards of topsoil, sand, compost, or leveling mix. Get weight, bag counts, and a bulk-vs-bagged cost estimate for your region.',
    icon: Shovel,
    href: '/tools/topsoil-calculator',
    isActive: true,
    estimatedTime: '2 minutes',
    category: 'Planning',
    featured: false,
    hasEmbed: false,
    relatedTools: ['lawn-size-calculator', 'aeration-timing-tool', 'overseeding-calculator'],
  },
  {
    id: 'grub-control-calculator',
    name: 'Grub Control Calculator',
    description:
      'Find out when to apply grub control in your area, preventive vs curative, and how many bags to buy. Get your region\'s treatment windows by ZIP, plus a symptom check to confirm you actually have grubs.',
    icon: Bug,
    href: '/tools/grub-control-calculator',
    isActive: true,
    estimatedTime: '2 minutes',
    category: 'Seasonal',
    featured: false,
    hasEmbed: false,
    relatedTools: ['herbicide-timing', 'weed-and-feed-calculator', 'soil-temperature'],
  },
  {
    id: 'dethatching-calculator',
    name: 'Dethatching Calculator',
    description:
      'Find out if your lawn actually needs dethatching, then get the right tool and your best window. Estimate your thatch layer and get a dethatch-or-wait verdict, power rake vs vertical mower guidance, and timing by ZIP and grass type.',
    icon: Wind,
    href: '/tools/dethatching-calculator',
    isActive: true,
    estimatedTime: '2 minutes',
    category: 'Seasonal',
    featured: false,
    hasEmbed: false,
    relatedTools: ['aeration-timing-tool', 'overseeding-calculator', 'soil-temperature'],
  },
  {
    id: 'weed-and-feed-calculator',
    name: 'Weed & Feed Calculator',
    description:
      'Find out when to apply weed & feed in your area and exactly how much to buy. Get spring and fall timing by ZIP, bags or quarts needed, spreader settings, and grass-safety checks.',
    icon: Bug,
    href: '/tools/weed-and-feed-calculator',
    isActive: true,
    estimatedTime: '2 minutes',
    category: 'Seasonal',
    featured: false,
    hasEmbed: false,
    relatedTools: ['herbicide-timing', 'overseeding-calculator', 'fertilizer-calculator', 'spreader-settings'],
  },
  {
    id: 'watering-schedule',
    name: 'Watering Schedule Generator',
    description:
      'Create a custom watering schedule based on your grass type, region, and current weather conditions.',
    icon: Droplets,
    href: '/tools/watering-schedule',
    isActive: true,
    estimatedTime: '2 minutes',
    category: 'Care',
    featured: false,
    hasEmbed: false,
    relatedTools: ['fertilizer-calculator', 'mowing-calculator', 'soil-temperature'],
  },
  {
    id: 'lawn-cost-estimator',
    name: 'Lawn Cost Estimator',
    description:
      'Calculate your annual lawn care costs including fertilizer, treatments, maintenance, and equipment. Compare DIY vs professional pricing.',
    icon: Wallet,
    href: '/tools/lawn-cost-estimator',
    isActive: true,
    estimatedTime: '3 minutes',
    category: 'Planning',
    featured: false,
    hasEmbed: false,
    relatedTools: ['lawn-size-calculator', 'fertilizer-calculator', 'watering-schedule', 'artificial-turf-cost-calculator'],
  },
  {
    id: 'mowing-calculator',
    name: 'Mowing Height Calculator',
    description:
      'Get the optimal mowing height and frequency recommendations for your specific grass type and season.',
    icon: Scissors,
    href: '/tools/mowing-calculator',
    isActive: true,
    estimatedTime: '1 minute',
    category: 'Care',
    featured: false,
    hasEmbed: false,
    relatedTools: ['watering-schedule', 'fertilizer-calculator', 'grass-comparison'],
  },
  {
    id: 'aeration-timing-tool',
    name: 'Aeration Timing Tool',
    description:
      'Find out when to aerate your lawn based on your location, grass type, and local climate.',
    icon: Calendar,
    href: '/tools/aeration-calculator',
    isActive: true,
    estimatedTime: '2 minutes',
    category: 'Seasonal',
    featured: false,
    hasEmbed: false,
    relatedTools: ['overseeding-calculator', 'topsoil-calculator', 'fertilizer-calculator'],
  },
  {
    id: 'herbicide-timing',
    name: 'Herbicide Timing Calculator',
    description:
      'Get precise timing recommendations for pre-emergent and post-emergent herbicide applications to control crabgrass and other weeds.',
    icon: Bug,
    href: '/tools/herbicide-timing',
    isActive: true,
    estimatedTime: '3 minutes',
    category: 'Seasonal',
    featured: false,
    hasEmbed: false,
    relatedTools: ['tank-mix-calculator', 'soil-temperature', 'fertilizer-calculator'],
  },
  {
    id: 'tank-mix-calculator',
    name: 'Tank Mix Calculator',
    description:
      'Calculate exactly how much herbicide and surfactant to mix per gallon of water. Spot and blanket rates for Tenacity, quinclorac, 2,4-D, and more, with step-by-step mixing order.',
    icon: FlaskConical,
    href: '/tools/tank-mix-calculator',
    isActive: true,
    estimatedTime: '1 minute',
    category: 'Seasonal',
    featured: false,
    hasEmbed: false,
    relatedTools: ['herbicide-timing', 'soil-temperature', 'lawn-size-calculator'],
  },
  {
    id: 'fertilizer-rate-calculator',
    name: 'Fertilizer Rate Calculator',
    description:
      'Enter the N-P-K on any bag and your lawn size. Get pounds per 1,000 sq ft, total pounds for the lawn, how many bags to buy, and the actual nutrients delivered.',
    icon: Scale,
    href: '/tools/fertilizer-rate-calculator',
    isActive: true,
    estimatedTime: '1 minute',
    category: 'Seasonal',
    featured: false,
    hasEmbed: false,
    relatedTools: ['fertilizer-calculator', 'lawn-size-calculator', 'soil-test-analyzer', 'spreader-settings'],
  },
  {
    id: 'fertilizer-schedule-calculator',
    name: 'Fertilizer Schedule Calculator',
    description:
      'Pick a product (Milorganite, Scotts Turf Builder, PGF, and more) and get the exact amount per application, bags to buy, and a year of application dates tuned to your ZIP and grass type.',
    icon: CalendarClock,
    href: '/tools/fertilizer-schedule-calculator',
    isActive: true,
    estimatedTime: '2 minutes',
    category: 'Seasonal',
    featured: false,
    hasEmbed: false,
    relatedTools: ['fertilizer-calculator', 'fertilizer-rate-calculator', 'soil-temperature', 'spreader-settings'],
  },
  {
    id: 'spreader-settings',
    name: 'Spreader Settings Lookup',
    description:
      'Pick your spreader and your bag. Get the dial number where the label publishes one, and the target pounds per 1,000 sq ft plus a calibration walk for every other combination.',
    icon: Scale,
    href: '/tools/spreader-settings',
    isActive: true,
    estimatedTime: '1 minute',
    category: 'Seasonal',
    featured: false,
    hasEmbed: false,
    relatedTools: [
      'fertilizer-rate-calculator',
      'weed-and-feed-calculator',
      'fertilizer-schedule-calculator',
    ],
  },
  {
    id: 'sprinkler-cost-calculator',
    name: 'Sprinkler Cost Calculator',
    description:
      'Estimate what an in-ground sprinkler system costs for your lawn size and region, pro or DIY, with zone counts and a full component breakdown: heads, valves, controller, and labor.',
    icon: Droplet,
    href: '/tools/sprinkler-cost-calculator',
    isActive: true,
    estimatedTime: '1 minute',
    category: 'Planning',
    featured: false,
    hasEmbed: false,
    relatedTools: ['watering-schedule', 'lawn-size-calculator', 'lawn-cost-estimator'],
  },
  {
    id: 'soil-test-analyzer',
    name: 'Soil Test Analyzer',
    description:
      'Upload your Logan Labs, Waypoint, or other soil test PDF and get an instant, grass-specific, plain-English analysis with lawn-size-adjusted product recommendations.',
    icon: TestTube,
    href: '/tools/soil-test-analyzer',
    isActive: true,
    estimatedTime: '2 minutes',
    category: 'Planning',
    featured: false,
    hasEmbed: false,
    relatedTools: ['lime-calculator', 'fertilizer-calculator', 'lawn-size-calculator'],
  },
  {
    id: 'lime-calculator',
    name: 'Lawn Lime Calculator',
    description:
      'Enter your soil pH, grass type, and soil texture to get pounds of lime (or sulfur, if your pH is high) per 1,000 sq ft, the total for your lawn, bag counts, and how to split it safely.',
    icon: FlaskConical,
    href: '/tools/lime-calculator',
    isActive: true,
    estimatedTime: '2 minutes',
    category: 'Planning',
    featured: false,
    hasEmbed: false,
    relatedTools: ['soil-test-analyzer', 'fertilizer-schedule-calculator', 'soil-temperature'],
  },
  {
    id: 'grass-comparison',
    name: 'Grass Comparison Tool',
    description:
      'Compare grass types side-by-side to find the best match for your lawn. See detailed differences in maintenance, climate tolerance, and care requirements.',
    icon: GitCompare,
    href: '/tools/grass-comparison',
    isActive: true,
    estimatedTime: '2 minutes',
    category: 'Planning',
    featured: false,
    hasEmbed: false,
    relatedTools: ['seeding-calculator', 'mowing-calculator', 'watering-schedule'],
  },
  {
    id: 'pest-identifier',
    name: 'Pest & Disease Identifier',
    description:
      'Upload photos and describe symptoms to identify common lawn pests and diseases, with treatment recommendations.',
    icon: Bug,
    href: '/tools/pest-identifier',
    isActive: false,
    estimatedTime: '3 minutes',
    category: 'Problems',
    featured: false,
    hasEmbed: false,
  },
  {
    id: 'ph-guide',
    name: 'Soil pH Guide',
    description:
      'Input your soil test results to get customized amendment recommendations for your grass type.',
    icon: TestTube,
    href: '/tools/ph-guide',
    isActive: false,
    estimatedTime: '3 minutes',
    category: 'Planning',
    featured: false,
    hasEmbed: false,
  },
  {
    id: 'soil-analyzer',
    name: 'Soil Health Analyzer',
    description:
      'Get detailed soil composition data for your location using USDA soil survey API. Includes pH, organic matter, and drainage recommendations.',
    icon: MapPin,
    href: '/tools/soil-analyzer',
    isActive: false,
    estimatedTime: '2 minutes',
    category: 'Planning',
    featured: false,
    hasEmbed: false,
  },
  ...PRODUCT_CALCULATOR_TOOLS,
]

export function getActiveTools(): ToolManifestEntry[] {
  return TOOLS_MANIFEST.filter((tool) => tool.isActive)
}

export function getRelatedTools(toolId: string): ToolManifestEntry[] {
  const tool = TOOLS_MANIFEST.find((t) => t.id === toolId)
  if (!tool?.relatedTools) return []
  return tool.relatedTools
    .map((id) => TOOLS_MANIFEST.find((t) => t.id === id))
    .filter((t): t is ToolManifestEntry => t != null && t.isActive)
}
